import turfSimplify from "@turf/simplify";
import GeoJSON from "ol/format/GeoJSON";
import { douglasPeucker } from "ol/geom/flat/simplify";
import { silentapifetch } from "vueforms/src/utils/apifetch"

let geoJsonFormat = new GeoJSON({
  dataProjection: "EPSG:3857"
});

/**
 * 
 * @param {Record<number,string>} colors 
 * @param {number} height 
 * @param {number} width 
 * @returns 
 */
  function getColorRampCanvas(colors, height,width) {
    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');
    console.assert(ctx !== null, "Canvas not supported");

    width = width || 256;
    canvas.width = width;
    canvas.height = height;

    let gradient = ctx.createLinearGradient(0, 0, width, 0);
    Object.keys(colors).map(e => Number(e)).sort().forEach((stop) => {
      gradient.addColorStop(+stop, colors[stop]);
    });

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    return ctx;
  }

  /**
   * 
   * @param {Record<number, string>} colors 
   * @param {number} width 
   * @returns 
   */
  function getColorRamp(colors, width) {
    return new Uint8Array(getColorRampCanvas(colors, 1, width).getImageData(0, 0, width || 256, 1).data);
}

function drawColorRampCanvas(canv, colors,x,y,w,h) {
  var canvas = canv;
  var ctx = canvas.getContext('2d');

  var gradient = ctx.createLinearGradient(0, 0, w, 0);
  for (var stop in colors) {
      gradient.addColorStop(+stop, colors[stop]);
  }

  ctx.fillStyle = gradient;
  ctx.fillRect(x, y, w, h);
}

function colorRampToIsoAreas(ramp) {
  let isoRamp = {};
  let keys = Object.keys(ramp).map(e => parseFloat(e));
  keys.sort();

  isoRamp[keys[0]] = ramp[keys[0]];
  for (var i = 1; i < keys.length - 1; i++){
    isoRamp["" + keys[i]] = ramp[keys[i]];
    isoRamp["" + (keys[i] - 1E-2)] = ramp[keys[i]];
  }
  isoRamp["" + keys[keys.length - 1]] = ramp[keys[keys.length - 1]];
  return isoRamp;
}

/**
 * 
 * @param {Record<string,string>} colorRamp 
 * @param {*} max 
 * @returns {Record<number, string>}
 */
function normalizeColorRamp(colorRamp, max) {
  /** @type {Record<number, string>} */
  let scaledRamp = {};
  for (let key in colorRamp) {
    let scaledVal = parseFloat(key) / max;
    scaledRamp[scaledVal] = colorRamp[key];
  }
  return scaledRamp;
}

function decodePixelToHeight(pixel) {
  return pixel[0] + (pixel[1] << 8) + (pixel[2] << 16);
}

function decodePixelToVelocity(pixel, maxVeloc) {
  return (pixel[2] / 255) * maxVeloc;
}

function decodePixelToWaterColor(pixels, data) {
  if (pixels[0][0] > 0 || pixels[0][1] > 0 || pixels[0][2] > 0) {
    let h = (pixels[0][0] + (pixels[0][1] << 8) + (pixels[0][2] << 16));
    let val = Math.round( Math.min(h, data.colRampMax) / data.colRampMax * data.colRampLength);

    val = (val > data.colRampLength ? data.colRampLength : val) * 4;
    return [
      data.colRamp[val],
      data.colRamp[val + 1],
      data.colRamp[val + 2],
      data.colRamp[val + 3]];
  }
  return [0,0,0,0];
}

function compareVersions(versionStringA, versionStringB) {
  const aArray = versionStringA.split(".");
  const bArray = versionStringB.split(".");
  for (let i = 0; i < 3; i++){
    let cmp = aArray[i] - bArray[i];
    if (cmp !== 0) {
      return cmp;
    }
  }
}

function isModelVersionAbove(modelVersion) {
  if (modelVersion != null && typeof modelVersion === "string") {
    return compareVersions(__VERSION__, modelVersion) < 0;
  }
  return false;
}

var extentChanged = function () {
  let oldExt = [null, null, null, null];
  return function (newExt) {
    let changed = false;
    for (let i = 0; i < newExt.length; i++) {
      if (newExt[i] !== oldExt[i]) {
        changed = true;
        break;
      }
    }
    oldExt = newExt;
    return changed;
  };
};

let clamp = (val, min, max) => {
  return Math.min(max, Math.max(val, min));
}

let tryCall = (instance, func, ...args) => {
  try {
    return instance[func].apply(instance, args);
  }
  catch (e) {
    console.error(e);
  }
  return null;
}
let context, canvas;
function getPixel(image, x, y) {
  if (!context) {
    canvas = document.createElement("canvas");
    context = canvas.getContext('2d');
    canvas.width = 1;
    canvas.height = 1;
    context.imageSmoothingEnabled = false;
  }

  context.clearRect(0, 0, 1, 1);
  context.drawImage(image, x, y, 1, 1, 0, 0, 1, 1);

  var imgData = context.getImageData(0, 0, 1, 1);
  return imgData.data;
}

function getBuildDate() {
  return __VERSION__ + "." + __BUILD_DATE__;
}

function insertVersionString() {
  let version = getBuildDate();
  document.querySelectorAll(".mapviewVersion").forEach((e) => {
    e.innerHTML = version;
  });
}

async function getPseudoMercatorGeoJSON(url, resolution) {
  let json = await silentapifetch(url).then(e => e.json());
  if(resolution)
    json = turfSimplify(json, {tolerance: resolution});
  return geoJsonFormat.readFeaturesFromObject(json);
}


async function getGeoJSON(url, resolution) {
  let data = await silentapifetch(url).then(e => e.json());
  return new GeoJSON({
    featureProjection: "EPSG:4326",
    dataProjection: "EPSG:3857"
  }).readFeaturesFromObject(data);
}

const kb = 1024, mb = kb * 1024;

function getPrettyFileSize(fileSize) {
  if (fileSize > kb) {
    if (fileSize > mb) {
      return (fileSize / mb).toLocaleString("de-De", { maximumFractionDigits: 2 }) + " MB";
    }
    return (fileSize / kb).toLocaleString("de-De", { maximumFractionDigits: 2 }) + " KB";
  }
  return fileSize + " Byte";
}

function simplifySeriesData(values, canvasWidth, canvasHeight) {
  if (!values || values.length === 0) {
    return [];
  }

  let max = -Number.MAX_VALUE,
    min = Number.MAX_VALUE,
    yScale;
  let xMax = values[values.length - 1].x,
    xMin = values[0].x,
    xScale = xMax - xMin;

  if (xScale === 0) {
      return values;
  }

  let flatCoords = [];
  flatCoords.length = values.length * 2;
  for (let i = 0; i < values.length; i++){
    max = Math.max(max, values[i].y);
    min = Math.min(min, values[i].y);
    flatCoords[i*2] = values[i].x;
    flatCoords[i*2+1] = values[i].y;
  }
  yScale = max - min;
  // durch 0 teilen ist böse ...
  if (yScale === 0) {
    return values;
  }

  // Transformiere Koordinaten in Viewspace
  for (let i = 0; i < flatCoords.length; i += 2){
    flatCoords[i] = (flatCoords[i] - xMin) / xScale * canvasWidth;
    flatCoords[i + 1] = (flatCoords[i + 1] - min) / yScale * canvasHeight;
  }

  let result = [];
  douglasPeucker(flatCoords, 0, flatCoords.length, 2, 1,result, 0);
  let seriesdata = [];
  for (let i = 0; i < result.length; i += 2){
    seriesdata.push({
      x: result[i] * xScale / canvasWidth + xMin,
      y: result[i + 1] * yScale / canvasHeight + min
    });
  }
  return seriesdata;
}

async function getAsyncElementSize(target){
  let promise = new Promise((resolve)=>{
    let timeout = setTimeout(()=>{
      obs.disconnect();
      resolve(target.getBoundingClientRect());
    }, 100)
    let obs = new MutationObserver(()=>{
        if(timeout){ //bei erneuter Änderung während Timer läuft, wird Timer gelöscht
          clearTimeout(timeout);
        }
        timeout = setTimeout(()=>{
          obs.disconnect();
          resolve(target.getBoundingClientRect());
        }, 100)
    });
   
    let observerConfig = {attributes: true, childList: true, subtree: true};
    obs.observe(target, observerConfig);
  });
  return promise;
}


export default {
  clamp: clamp,
  decodePixelToHeight: decodePixelToHeight,
  decodePixelToVelocity: decodePixelToVelocity,
  decodePixelToWaterColor: decodePixelToWaterColor,
  drawColorRampCanvas: drawColorRampCanvas,
  colorRampToIsoAreas: colorRampToIsoAreas,
  normalizeColorRamp: normalizeColorRamp,
  getColorRamp: getColorRamp,
  getColorRampCanvas: getColorRampCanvas,
  extentChanged: extentChanged,
  isModelVersionAbove: isModelVersionAbove,
  compareVersions: compareVersions,
  tryCall: tryCall,
  getPixel: getPixel,
  getBuildDate: getBuildDate,
  getPseudoMercatorGeoJSON: getPseudoMercatorGeoJSON,
  getGeoJSON: getGeoJSON,
  insertVersionString: insertVersionString,
  getPrettyFileSize: getPrettyFileSize,
  simplifySeriesData: simplifySeriesData,
  getAsyncElementSize: getAsyncElementSize
}